import { useEffect } from 'react';
import { Modal, Spin, Typography, Descriptions } from 'antd';
import { observer } from 'mobx-react';
import style from './style.module.scss';
import { useClientStore } from '../../../../../contexts/mobx';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    MessageOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons';
import { Tag } from '../../../../../components/tag';

const { Text, Title } = Typography;

interface SMSDeliveryModalProps {
    isVisible: boolean;
    onClose: () => void;
    messageId?: string;
}

export const SMSDeliveryModal = ({
    isVisible,
    onClose,
    messageId,
}: SMSDeliveryModalProps) => {
    const clientStore = useClientStore();

    useEffect(() => {
        if (isVisible && messageId) {
            clientStore.fetchSMSDeliveryStatus(messageId);
        }
    }, [isVisible, messageId, clientStore]);

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);

        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        };

        return new Intl.DateTimeFormat('en-US', options).format(date);
    };

    const getStatusTag = (status: string | null) => {
        if (!status)
            return (
                <Tag variant="grey" icon={<QuestionCircleOutlined />}>
                    Unknown
                </Tag>
            );

        switch (status.toLowerCase()) {
            case 'success':
                return (
                    <Tag variant="success" icon={<CheckCircleOutlined />}>
                        Delivered
                    </Tag>
                );
            case 'failure':
                return (
                    <Tag variant="error" icon={<CloseCircleOutlined />}>
                        Failed
                    </Tag>
                );
            default:
                return (
                    <Tag variant="grey" icon={<QuestionCircleOutlined />}>
                        {status}
                    </Tag>
                );
        }
    };

    return (
        <Modal
            title={
                <div className={style.modalHeader}>
                    <MessageOutlined className={style.smsIcon} />
                    <Title level={4} className={style.modalTitle}>
                        SMS Delivery Status
                    </Title>
                </div>
            }
            open={isVisible}
            onCancel={onClose}
            footer={null}
            centered
            width={500}
            className={style.smsDeliveryModal}
        >
            {clientStore.isFetchingSMSDeliveryStatus ? (
                <div className={style.loadingContainer}>
                    <Spin size="large" />
                </div>
            ) : clientStore.smsDeliveryStatus ? (
                <Descriptions
                    bordered
                    column={1}
                    className={style.detailsContainer}
                >
                    <Descriptions.Item label="Status">
                        {getStatusTag(clientStore.smsDeliveryStatus.status)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Provider">
                        {clientStore.smsDeliveryStatus.provider || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Provider Response">
                        {clientStore.smsDeliveryStatus.providerResponse ||
                            'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Sent At">
                        {clientStore.smsDeliveryStatus.createdAt
                            ? formatDate(
                                  clientStore.smsDeliveryStatus.createdAt
                              )
                            : 'N/A'}
                    </Descriptions.Item>
                </Descriptions>
            ) : (
                <div className={style.noDataContainer}>
                    <Text type="secondary">
                        No delivery information available for this message.
                    </Text>
                </div>
            )}
        </Modal>
    );
};

export default observer(SMSDeliveryModal);
